import React, { useState, useEffect, useCallback, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import Dashboard from "./pages/Dashboard";
import Login from "./pages/Login";
import ActivityLogPage from "./pages/ActivityLogPage";
import PopOutTickLog from "./pages/PopOutTickLog";
import DOMAnalysis from "./pages/DOMAnalysis";
import OptionChain from "./pages/OptionChain";
import AlgoSetupPopOut from "./pages/AlgoSetupPopOut";
import BrokerSelector from "./components/brokers/BrokerSelector";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

// Create axios instance with retry logic
export const apiClient = axios.create({
  baseURL: API,
  timeout: 60000, // Increased to 60 seconds for slow server responses
});

// Retry configuration
const MAX_RETRIES = 3;
const RETRY_DELAY = 2000; // 2 seconds

// Helper function to delay
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Add JWT token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Initialize retry count
  config._retryCount = config._retryCount || 0;
  return config;
});

// Handle 401 responses, session validation, and auto-retry on server errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    
    // Check if session was invalidated (logged in from another device)
    if (error.response?.status === 401) {
      const currentPath = window.location.pathname;
      if (currentPath !== '/' && currentPath !== '/login') {
        // Clear local storage and redirect to login
        localStorage.removeItem('auth_token');
        localStorage.removeItem('session_id');
        localStorage.removeItem('user');
        toast.error('Session expired. Please login again.', { duration: 5000 });
        window.location.href = '/';
      }
      return Promise.reject(error);
    }
    
    // Auto-retry on 5xx server errors or network errors
    const isServerError = error.response?.status >= 500;
    const isNetworkError = !error.response && error.code !== 'ECONNABORTED';
    
    if ((isServerError || isNetworkError) && config._retryCount < MAX_RETRIES) {
      config._retryCount += 1;
      console.log(`Request failed, retrying (${config._retryCount}/${MAX_RETRIES})...`);
      
      // Silent retry - no toast notifications
      
      // Wait before retrying
      await delay(RETRY_DELAY * config._retryCount);
      
      // Retry the request
      return apiClient(config);
    }
    
    return Promise.reject(error);
  }
);

// BroadcastChannel for session/logout sync across tabs and pop-out windows
const SESSION_BROADCAST_CHANNEL = 'alpha_session_sync';

// Broadcast logout event to all windows (including pop-outs)
export const broadcastLogout = (reason = 'manual') => {
  try {
    const channel = new BroadcastChannel(SESSION_BROADCAST_CHANNEL);
    channel.postMessage({ type: 'logout', reason, timestamp: Date.now() });
    channel.close();
  } catch (err) {
    // BroadcastChannel not supported - use localStorage fallback
    localStorage.setItem('session_logout_event', JSON.stringify({ 
      reason, 
      timestamp: Date.now() 
    }));
  }
};

// Session validation function - checks if user was logged out from another device
export const validateSession = async () => {
  const token = localStorage.getItem('auth_token');
  if (!token) return false;
  
  try {
    const response = await apiClient.get('/user/validate-session');
    
    // Multi-tab from same IP is valid - don't show any notification
    if (response.data.valid) {
      return true;
    }
    
    // Only handle actual session expiry (different device login)
    if (!response.data.valid && response.data.reason === 'session_expired') {
      // Check if we already showed this notification recently (prevent spam)
      const lastNotification = localStorage.getItem('session_expired_notif_time');
      const now = Date.now();
      
      if (lastNotification && (now - parseInt(lastNotification)) < 10000) {
        // Already showed notification in last 10 seconds, skip
        return false;
      }
      
      // Mark that we showed notification
      localStorage.setItem('session_expired_notif_time', now.toString());
      
      // Session is invalid - user logged in from another device
      localStorage.removeItem('auth_token');
      localStorage.removeItem('session_id');
      localStorage.removeItem('user');
      
      // Broadcast logout to all pop-out windows
      broadcastLogout(response.data.reason || 'session_expired');
      
      toast.error('You have been logged out because you logged in from another device.', {
        duration: 8000,
        icon: '🔒',
        id: 'session-expired-toast'  // Prevent duplicate toasts
      });
      
      window.location.href = '/';
      return false;
    }
    
    return !response.data.valid ? false : true;
  } catch (err) {
    // Network error - don't treat as session invalid
    return true;
  }
};

// ==================== MARKET HOURS CONFIGURATION ====================
const MARKET_HOURS = {
  NSE: { start: { hour: 9, minute: 15 }, end: { hour: 15, minute: 30 } },
  BSE: { start: { hour: 9, minute: 15 }, end: { hour: 15, minute: 30 } },
  NFO: { start: { hour: 9, minute: 15 }, end: { hour: 15, minute: 30 } },
  BFO: { start: { hour: 9, minute: 15 }, end: { hour: 15, minute: 30 } },
  MCX: { start: { hour: 9, minute: 0 }, end: { hour: 23, minute: 54 } },
  CDS: { start: { hour: 9, minute: 0 }, end: { hour: 17, minute: 0 } },
};

// Check if time is within market hours (including weekend check)
const isWithinMarketHours = (timestamp, exchange) => {
  if (!timestamp || !exchange) return false; // Default to false for safety
  const date = new Date(timestamp);
  
  // Weekend check - Saturday=6, Sunday=0
  const dayOfWeek = date.getDay();
  if (dayOfWeek === 0 || dayOfWeek === 6) {
    return false; // Markets closed on weekends
  }
  
  const istOffset = 5.5 * 60;
  const utcMinutes = date.getUTCHours() * 60 + date.getUTCMinutes();
  const istMinutes = utcMinutes + istOffset;
  const istHour = Math.floor((istMinutes % (24 * 60)) / 60);
  const istMinute = istMinutes % 60;
  
  const hours = MARKET_HOURS[exchange] || MARKET_HOURS['NSE'];
  const startMinutes = hours.start.hour * 60 + hours.start.minute;
  const endMinutes = hours.end.hour * 60 + hours.end.minute;
  const currentMinutes = istHour * 60 + istMinute;
  
  if (exchange === 'MCX' && endMinutes < startMinutes) {
    return currentMinutes >= startMinutes || currentMinutes <= endMinutes;
  }
  return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
};

// Get exchange from instrument
const getExchangeFromInstrument = (instrument) => {
  if (!instrument) return 'NSE';
  const exchange = (instrument.exchange || '').toUpperCase();
  const segment = (instrument.segment || '').toUpperCase();
  const tradingsymbol = (instrument.tradingsymbol || '').toUpperCase();
  
  if (exchange.includes('MCX') || segment.includes('MCX')) return 'MCX';
  const mcxPatterns = ['GOLD', 'SILVER', 'CRUDE', 'COPPER', 'ZINC', 'LEAD', 'ALUMIN', 'NICKEL', 'NATGAS', 'COTTON', 'CRUDEOIL'];
  for (const pattern of mcxPatterns) {
    if (tradingsymbol.includes(pattern)) return 'MCX';
  }
  if (exchange.includes('CDS') || segment.includes('CDS')) return 'CDS';
  if (exchange.includes('NFO') || segment.includes('NFO')) return 'NFO';
  if (exchange.includes('BFO') || segment.includes('BFO')) return 'BFO';
  if (exchange.includes('BSE') || segment.includes('BSE')) return 'BSE';
  return 'NSE';
};

// Helper to fetch ticks via REST API
const fetchTicksAPI = async () => {
  try {
    const response = await apiClient.get("/ticks");
    return {
      ticks: response.data.ticks || [],
      market_status: response.data.market_status || 'unknown',
      is_weekend: response.data.is_weekend || false
    };
  } catch (error) {
    console.error("Failed to fetch ticks:", error);
    return { ticks: [], market_status: 'error', is_weekend: false };
  }
};

// App Context
export const AppContext = React.createContext(null);

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const isAuthenticatedRef = useRef(false);  // Ref to access isAuthenticated in callbacks
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tickData, setTickData] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [triggeredAlerts, setTriggeredAlerts] = useState([]);
  const [wsConnected, setWsConnected] = useState(false);
  const [subscribedTokens, setSubscribedTokens] = useState([]);
  const subscribedTokensRef = useRef([]);  // Ref to access subscribedTokens in callbacks
  const [marketStatus, setMarketStatus] = useState({ isOpen: true, isReceivingData: false });
  const [kiteConnected, setKiteConnected] = useState(false);  // New: Per-user Kite connection state
  const [showKiteConnect, setShowKiteConnect] = useState(false);  // New: Show Kite connect modal
  const [connectedBroker, setConnectedBroker] = useState(null);  // Track which broker: 'zerodha' | null
  const [isSwitchingBroker, setIsSwitchingBroker] = useState(false);  // Flag to prevent auto-reconnect during switch
  const isSwitchingBrokerRef = useRef(false);  // Ref to access isSwitchingBroker in callbacks
  const [isPageVisible, setIsPageVisible] = useState(true);  // Track page visibility
  const [showSebiDisclaimer, setShowSebiDisclaimer] = useState(false);  // SEBI risk disclaimer popup
  const [pendingLoginData, setPendingLoginData] = useState(null);  // Store login data until disclaimer accepted
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);  // Track reconnection attempts
  const wasConnectedBeforeRef = useRef(false);  // Track if WS was connected before (for reconnection detection)
  const audioRef = useRef(null);
  const sessionCheckIntervalRef = useRef(null);
  const backgroundTickBufferRef = useRef({});  // Buffer ticks when page is hidden
  const lastVisibleTimestampRef = useRef(Date.now());  // Track when page was last visible
  
  // Keep isAuthenticatedRef in sync
  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);
  
  // Keep isSwitchingBrokerRef in sync
  useEffect(() => {
    isSwitchingBrokerRef.current = isSwitchingBroker;
  }, [isSwitchingBroker]);
  
  // Keep subscribedTokensRef in sync with subscribedTokens state
  useEffect(() => {
    subscribedTokensRef.current = subscribedTokens;
  }, [subscribedTokens]);
  
  // ==================== PAGE VISIBILITY TRACKING ====================
  // Ensure NO ticks are missed when switching browser tabs
  useEffect(() => {
    const handleVisibilityChange = async () => {
      const isVisible = document.visibilityState === 'visible';
      setIsPageVisible(isVisible);
      
      if (isVisible) {
        // Page became visible - fetch ALL missed ticks from backend
        const timeSinceHidden = Date.now() - lastVisibleTimestampRef.current;
        console.log(`[Visibility] Page visible again after ${Math.round(timeSinceHidden / 1000)}s`);
        
        // Always fetch missed ticks if we have subscribed tokens (even for short tab switches)
        if (timeSinceHidden > 500 && subscribedTokens.length > 0) {
          await fetchMissedTicks(lastVisibleTimestampRef.current, subscribedTokens);
        }
        
        // Apply any buffered ticks immediately
        if (Object.keys(backgroundTickBufferRef.current).length > 0) {
          setTickData(prev => ({
            ...prev,
            ...backgroundTickBufferRef.current
          }));
          backgroundTickBufferRef.current = {};
        }
      } else {
        // Page hidden - record timestamp for precise missed tick calculation
        lastVisibleTimestampRef.current = Date.now();
        console.log('[Visibility] Page hidden - tracking timestamp for missed ticks');
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [wsConnected, subscribedTokens]);
  
  // Fetch ALL missed ticks from backend when returning to tab - ensures ZERO data loss
  const fetchMissedTicks = useCallback(async (sinceTimestamp, tokens) => {
    try {
      // Use the new /ticks/missed endpoint to get ALL ticks since we left
      const response = await apiClient.post('/ticks/missed', {
        tokens: tokens,
        since_timestamp_ms: sinceTimestamp,
        limit: 5000
      });
      
      if (response.data.total_missed > 0) {
        const results = response.data.results;
        let totalMerged = 0;
        
        // Update tickData with latest tick for each token
        const latestTickMap = {};
        
        // Merge ALL missed ticks into backgroundTickHistoryRef
        for (const [tokenStr, data] of Object.entries(results)) {
          const token = parseInt(tokenStr);
          const missedTicks = data.ticks || [];
          
          if (missedTicks.length > 0) {
            // Filter out dummy ticks with 0 bid/ask
            const validTicks = missedTicks.filter(t => 
              t.bid_price && t.bid_price > 0 && t.ask_price && t.ask_price > 0
            );
            
            if (validTicks.length === 0) continue;
            
            // Initialize history if needed
            if (!backgroundTickHistoryRef.current[token]) {
              backgroundTickHistoryRef.current[token] = [];
            }
            
            // Get existing timestamps to avoid duplicates
            const existingTimestamps = new Set(
              backgroundTickHistoryRef.current[token].map(t => t.timestamp_ms)
            );
            
            // Add only new valid ticks
            const newTicks = validTicks.filter(t => !existingTimestamps.has(t.timestamp_ms));
            
            if (newTicks.length > 0) {
              // Merge and sort by timestamp (newest first)
              backgroundTickHistoryRef.current[token] = [
                ...newTicks,
                ...backgroundTickHistoryRef.current[token]
              ].sort((a, b) => (b.timestamp_ms || 0) - (a.timestamp_ms || 0))
               .slice(0, 50000); // Keep max 50k per token
              
              totalMerged += newTicks.length;
              
              // Get latest tick for tickData
              latestTickMap[token] = newTicks[0]; // First is newest after sort
            }
          }
        }
        
        // Update tickData with latest ticks
        if (Object.keys(latestTickMap).length > 0) {
          setTickData(prev => ({ ...prev, ...latestTickMap }));
        }
        
        console.log(`[Visibility] Recovered ${totalMerged} missed ticks from ${response.data.tokens_checked} tokens`);
      } else {
        console.log('[Visibility] No missed ticks to recover');
      }
    } catch (error) {
      console.error('[Visibility] Failed to fetch missed ticks:', error);
      // Fallback to simple /ticks endpoint
      try {
        const response = await apiClient.get('/ticks');
        if (response.data.ticks) {
          const tickMap = {};
          response.data.ticks.forEach(tick => {
            // Filter out dummy ticks
            if (!tick.bid_price || tick.bid_price === 0 || 
                !tick.ask_price || tick.ask_price === 0) {
              return;
            }
            tickMap[tick.instrument_token] = tick;
          });
          setTickData(prev => ({ ...prev, ...tickMap }));
        }
      } catch (e) {
        console.error('[Visibility] Fallback also failed:', e);
      }
    }
  }, []);
  
  // ==================== SESSION VALIDATION (One User, One Device) ====================
  // Check session validity every 30 seconds to detect login from another device
  useEffect(() => {
    if (isAuthenticated) {
      // Initial check
      validateSession();
      
      // Set up periodic check every 30 seconds
      sessionCheckIntervalRef.current = setInterval(() => {
        validateSession();
      }, 30000); // Check every 30 seconds
      
      return () => {
        if (sessionCheckIntervalRef.current) {
          clearInterval(sessionCheckIntervalRef.current);
        }
      };
    }
  }, [isAuthenticated]);
  
  // ==================== BACKGROUND TICK COLLECTION ====================
  // This runs continuously even when Tick Log is closed or tab is inactive
  const [instruments, setInstruments] = useState({});
  const backgroundTickHistoryRef = useRef({});
  const lastStoredIndexRef = useRef({});
  const backgroundCollectionActiveRef = useRef(false);
  const [backgroundStats, setBackgroundStats] = useState({ ticksCollected: 0, lastSaveTime: null });
  
  // Backend background service status
  const [backendServiceStatus, setBackendServiceStatus] = useState({
    active: false,
    isConnected: false,
    totalTicks: 0,
    message: ''
  });
  
  // Fetch backend service status periodically
  const fetchBackendServiceStatus = useCallback(async () => {
    try {
      const response = await apiClient.get("/background-service/status");
      setBackendServiceStatus({
        active: response.data.active,
        isConnected: response.data.websocket_connected,
        totalTicks: response.data.total_ticks_received || 0,
        message: response.data.message || '',
        uptime: response.data.uptime || '0h 0m',
        ticksPerMinute: response.data.ticks_per_minute || 0
      });
    } catch (err) {
      console.error('Error fetching backend service status:', err);
    }
  }, []);
  
  // Store ticks to backend (non-blocking background operation)
  const storeTicksToBackend = useCallback(async () => {
    try {
      const batches = [];
      const tickHistory = backgroundTickHistoryRef.current;
      const lastIdx = lastStoredIndexRef.current;
      
      for (const [tokenStr, ticks] of Object.entries(tickHistory)) {
        const token = parseInt(tokenStr);
        const lastStoredIdx = lastIdx[token] || 0;
        const newTicks = ticks.slice(lastStoredIdx);
        
        if (newTicks.length > 0) {
          batches.push({
            token,
            symbol: instruments[token]?.tradingsymbol || `Token_${token}`,
            ticks: newTicks
          });
          lastStoredIndexRef.current[token] = ticks.length;
        }
      }
      
      if (batches.length > 0) {
        await apiClient.post('/history/ticks/store', { batches });
        setBackgroundStats(prev => ({ 
          ...prev, 
          lastSaveTime: new Date().toISOString(),
          ticksCollected: Object.values(tickHistory).reduce((sum, arr) => sum + arr.length, 0)
        }));
        console.log(`[BackgroundTicks] Stored ${batches.length} batches to backend`);
      }
    } catch (err) {
      console.error('[BackgroundTicks] Error storing ticks:', err);
    }
  }, [instruments]);
  
  // Fetch instruments for mapping tokens to symbols - Zerodha only
  const fetchInstruments = useCallback(async (broker = null) => {
    try {
      // Use Zerodha/Kite instruments with instrument_token format
      const response = await apiClient.get("/instruments");
      const instrumentMap = {};
      (response.data.instruments || []).forEach(inst => {
        instrumentMap[inst.instrument_token] = {
          ...inst,
          broker: 'zerodha'
        };
      });
      setInstruments(instrumentMap);
      console.log(`[Zerodha] Loaded ${Object.keys(instrumentMap).length} instruments`);
    } catch (err) {
      console.error(`[Zerodha] Error fetching instruments:`, err);
    }
  }, []);

  // Check if user has premium access
  const hasPremium = useCallback(() => {
    if (!currentUser) return false;
    return currentUser.subscription_status === 'active';
  }, [currentUser]);

  // Check market status
  const checkMarketStatus = useCallback(async () => {
    try {
      const response = await apiClient.get("/market/status");
      setMarketStatus({
        isOpen: response.data.is_receiving_live_data || Object.values(response.data.exchanges || {}).some(e => e.is_open),
        isReceivingData: response.data.is_receiving_live_data,
        currentTime: response.data.current_time_ist,
        message: response.data.message
      });
    } catch (error) {
      console.error("Market status check failed:", error);
    }
  }, []);

  // Check authentication status (JWT based)
  const checkAuth = useCallback(async () => {
    try {
      // Check for JWT token in localStorage
      const token = localStorage.getItem('auth_token');
      const userStr = localStorage.getItem('user');
      
      if (token && userStr) {
        const user = JSON.parse(userStr);
        // Verify token with backend
        try {
          const response = await apiClient.get("/user/me");
          setCurrentUser(response.data);
          setIsAuthenticated(true);
          
          // Check broker connection status - Zerodha only
          const isKiteConnected = response.data.kite_connected || false;
          
          setKiteConnected(isKiteConnected);
          
          // Track Zerodha connection
          if (isKiteConnected) {
            setConnectedBroker('zerodha');
            localStorage.setItem('active_broker', 'zerodha');
          } else {
            setConnectedBroker(null);
            localStorage.removeItem('active_broker');
          }
          
          if (!isKiteConnected) {
            setShowKiteConnect(true);
          }
        } catch (err) {
          // Token invalid, clear storage
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user');
          setIsAuthenticated(false);
          setCurrentUser(null);
          setKiteConnected(false);
        }
      } else {
        setIsAuthenticated(false);
        setCurrentUser(null);
        setKiteConnected(false);
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      setIsAuthenticated(false);
      setCurrentUser(null);
      setKiteConnected(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initialize audio for alerts
  useEffect(() => {
    audioRef.current = new Audio("data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleB4Aabfs6nlhBi2R2+N4bxUEQYvl6IFbAACE4+V0WAAAbebkcFIAAHTp4WtIAACE7+JlPQAAYfDiYDMAAGHx4l8wAABf8+JeMAAAdPLiXjAAOXvv4l4wAAuD7eJeMACch+biXjAAq4Pi4l4wAKqE3eJeMACpiNfiXjAAqY3Q4l4wAKqRyeJeMACpl8PiXjAAqJq84l4wAKedsOJeMACnnaTiXjAAp56Y4l4wAKegjeJeMACnoILiXjAAp6F34l4wAKehbeJeMACnoWPiXjAAp6JZ4l4wAKiiUOJeMACoo0fiXjAAqKM+4l4wAKijNeJeMACopC3iXjAAqKQl4l4wAKilHeJeMACopRXiXjAAqKYO4l4wAKimB+JeMACnpwHiXjAAp6f74F4wAKen9eBeMACnqO/gXjAAp6nq4F4wAKep5eBeMACnquDgXjAAp6rc4F4wAKer2OBeMACnrNTgXjAAp63Q4F4wAKetzOBeMACnrsngXjAAp6/G4F4wAKeww+BeMACnscDgXjAAp7K+4F4wAKezu+BeMACntLngXjAAp7W34F4wAKe2teBeMACnt7PgXjAAp7ix4F4wAKe5sOBeMACnuq7gXjAAp7ut4F4wAKe8rOBeMACnvavgXjAAp76q4F4wAKe/qeBeMACowKjgXjAAqMGo4F4wAKjCp+BeMACow6fgXjAAqMSn4F4wAKjFp+BeMACoxqfgXjAAqMen4F4wAKjIp+BeMACoyafgXjAAqMqn4F4wAKjLp+BeMACozKfgXjAAqM2n4F4wAKjOp+BeMACoz6fgXjAAqNCn4F4wAKjRp+BeMACo0qfgXjAAqNOn4F4wAKjUp+BeMACo1afgXjAAqNan4F4wAKjXp+BeMACo2KfgXjAAqNmn4F4wAKjap+BeMACo26fgXjAAqNyn4F4wAKjdp+BeMACo3qfgXjAAqN+n4F4wAKjgp+BeMACo4afgXjAAqOKn4F4wAKjjp+BeMACo5KfgXjAAqOWn4F4wAKjmp+BeMACo56fgXjAAqOin4F4wAKjpp+BeMACo6qfgXjAAqOun4F4wAKjsp+BeMACo7afgXjAAqO6n4F4wAKjvp+BeMACo8KfgXjAAqPGn4F4wAA==");
  }, []);

  // Play alert sound
  const playAlertSound = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(console.error);
    }
  }, []);

  // Show browser notification
  const showNotification = useCallback((title, body) => {
    if (Notification.permission === "granted") {
      new Notification(title, { body, icon: "/favicon.ico" });
    } else if (Notification.permission !== "denied") {
      Notification.requestPermission().then(permission => {
        if (permission === "granted") {
          new Notification(title, { body, icon: "/favicon.ico" });
        }
      });
    }
  }, []);

  // WebSocket connection - STABLE connection that works even when tab is hidden
  const connectWebSocket = useCallback(() => {
    // CRITICAL: Check ALL states that indicate an active connection
    // This prevents opening multiple connections
    if (!isAuthenticated) return;
    
    // Check if we already have an active or connecting WebSocket
    if (wsRef.current) {
      const state = wsRef.current.readyState;
      if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) {
        console.log(`[WebSocket] Already ${state === WebSocket.OPEN ? 'connected' : 'connecting'}, skipping new connection`);
        return;
      }
      // If CLOSING or CLOSED, clean up the ref
      wsRef.current = null;
    }

    try {
      // Include auth token for per-user session isolation
      const authToken = localStorage.getItem('auth_token');
      const wsUrl = authToken 
        ? `${WS_URL}/api/ws/ticks?token=${encodeURIComponent(authToken)}`
        : `${WS_URL}/api/ws/ticks`;
      
      console.log('[WebSocket] Creating new connection...');
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log("WebSocket connected (isolated session)");
        setWsConnected(true);
        
        // Reset reconnection attempts on successful connection
        reconnectAttemptsRef.current = 0;
        
        // Check if this is a reconnection (server restart scenario)
        if (wasConnectedBeforeRef.current) {
          console.log("WebSocket reconnected after disconnect - attempting auto re-subscribe");
          // Silent reconnection - no toast, dot indicator shows status
          
          // Get current subscribed tokens from state
          const tokensToResubscribe = subscribedTokensRef.current || [];
          
          if (tokensToResubscribe.length > 0) {
            // Auto re-subscribe silently in background
            apiClient.post("/subscribe", {
              instrument_tokens: tokensToResubscribe,
              mode: "full"
            }).then(() => {
              console.log(`Auto re-subscribed to ${tokensToResubscribe.length} instruments`);
            }).catch((error) => {
              console.error("Auto re-subscribe failed:", error);
              // Only show error toast if re-subscribe fails - this is important
              toast.error("Tick storage paused. Please refresh to resume.", { 
                duration: 8000,
                action: {
                  label: "Refresh",
                  onClick: () => window.location.reload()
                }
              });
            });
          }
        }
        
        // Mark that we've been connected (for future reconnection detection)
        wasConnectedBeforeRef.current = true;
        
        console.log("WebSocket connected - real-time data active");
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          // Update last tick time for stale connection detection
          if (ws._lastTickTime !== undefined) {
            ws._lastTickTime = Date.now();
          }
          
          if (message.type === "tick") {
            const token = message.data.instrument_token;
            const newTick = message.data;
            
            // FILTER OUT DUMMY TICKS - ticks with 0 bid/ask prices are invalid
            // These can occur outside market hours or during connection issues
            if (!newTick.bid_price || newTick.bid_price === 0 || 
                !newTick.ask_price || newTick.ask_price === 0) {
              // Skip dummy tick - don't add to history or display
              return;
            }
            
            // ALWAYS add EVERY valid tick to background history (for Tick Log)
            // NO deduplication - keep ALL ticks from Zerodha exactly as received
            // UNLIMITED buffer - keep all ticks without limit
            if (backgroundTickHistoryRef.current) {
              backgroundTickHistoryRef.current[token] = backgroundTickHistoryRef.current[token] || [];
              // Add new tick at the beginning (newest first) - NO LIMIT
              backgroundTickHistoryRef.current[token].unshift(newTick);
              
              // Debug: Log tick count for this token
              if (backgroundTickHistoryRef.current[token].length % 50 === 0) {
                console.log(`[WS] Token ${token} tick count: ${backgroundTickHistoryRef.current[token].length}`);
              }
            }
            
            // Update tickData IMMEDIATELY for real-time display (no batching)
            // This ensures UI shows every tick as it arrives
            setTickData(prev => {
              const prevTick = prev[token];
              return {
                ...prev,
                [token]: {
                  ...newTick,
                  priceChange: prevTick ? 
                    (newTick.last_price > prevTick.last_price ? "up" : 
                     newTick.last_price < prevTick.last_price ? "down" : null) : null
                }
              };
            });
          } else if (message.type === "initial") {
            const initialData = {};
            message.data.forEach(tick => {
              // Filter out dummy ticks from initial data too
              if (!tick.bid_price || tick.bid_price === 0 || 
                  !tick.ask_price || tick.ask_price === 0) {
                return; // Skip dummy tick
              }
              
              initialData[tick.instrument_token] = tick;
              // Also populate background history
              if (backgroundTickHistoryRef.current) {
                backgroundTickHistoryRef.current[tick.instrument_token] = 
                  backgroundTickHistoryRef.current[tick.instrument_token] || [];
                backgroundTickHistoryRef.current[tick.instrument_token].unshift(tick);
              }
            });
            setTickData(initialData);
          } else if (message.type === "alert") {
            playAlertSound();
            showNotification("Trading Alert", message.message);
            toast.warning(message.message, { duration: 10000 });
            setTriggeredAlerts(prev => [...prev, message]);
          } else if (message.type === "heartbeat") {
            // WebSocket heartbeat - connection is alive
            // Send pong back to keep connection active
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: "pong" }));
            }
          }
        } catch (error) {
          console.error("WebSocket message error:", error);
        }
      };

      ws.onclose = (event) => {
        console.log("WebSocket disconnected - code:", event.code, "reason:", event.reason);
        setWsConnected(false);
        
        // Clear ping interval
        if (ws._pingInterval) {
          clearInterval(ws._pingInterval);
        }
        
        // Clear stale check interval
        if (ws._staleCheckInterval) {
          clearInterval(ws._staleCheckInterval);
        }
        
        // Attempt reconnection only if not switching broker and under max attempts
        // Use refs to get current values (closures may have stale values)
        if (isAuthenticatedRef.current && !isSwitchingBrokerRef.current) {
          reconnectAttemptsRef.current += 1;
          const attempt = reconnectAttemptsRef.current;
          
          // Exponential backoff: 2s, 4s, 8s, 16s, max 30s
          const delay = Math.min(2000 * Math.pow(2, attempt - 1), 30000);
          
          // Silent reconnection - no toast, just use dot indicator
          // Only log to console for debugging
          console.log(`Reconnecting silently (attempt ${attempt})...`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log(`Attempting WebSocket reconnection (attempt ${attempt})...`);
            reconnectTimeoutRef.current = null;
            connectWebSocket();
          }, delay);
        } else {
          console.log(`WebSocket closed but not reconnecting: isAuthenticated=${isAuthenticatedRef.current}, isSwitchingBroker=${isSwitchingBrokerRef.current}`);
        }
      };

      ws.onerror = (error) => {
        console.error("WebSocket error:", error);
        setWsConnected(false);
        
        // Also attempt reconnection on error if not already scheduled
        // Use refs to get current values
        if (isAuthenticatedRef.current && !isSwitchingBrokerRef.current && !reconnectTimeoutRef.current) {
          reconnectAttemptsRef.current += 1;
          const delay = Math.min(2000 * Math.pow(2, reconnectAttemptsRef.current - 1), 30000);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log("Attempting WebSocket reconnection after error...");
            reconnectTimeoutRef.current = null;
            connectWebSocket();
          }, delay);
        }
      };
      
      // Keep-alive ping every 5 seconds to prevent connection timeout
      ws._pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "ping", ts: Date.now() }));
        }
      }, 5000);
      
      // Stale connection detection - if no tick received for extended period during market hours
      // More lenient to avoid unnecessary reconnects during high-load periods
      ws._lastTickTime = Date.now();
      ws._staleCheckInterval = setInterval(() => {
        const now = Date.now();
        const timeSinceLastTick = now - ws._lastTickTime;
        
        // Check if it's market hours - includes MCX which runs until 11:54 PM
        // NSE/NFO: 9:15 AM - 3:30 PM, MCX: 9:00 AM - 11:54 PM
        const hour = new Date().getHours();
        const minute = new Date().getMinutes();
        const isNSEHours = hour >= 9 && (hour < 15 || (hour === 15 && minute <= 30));
        const isMCXHours = hour >= 9 && (hour < 23 || (hour === 23 && minute <= 54));
        const isMarketHours = isNSEHours || isMCXHours;
        
        // Only check for stale connection during market hours and if no tick for 15+ seconds
        // Increased threshold to reduce unnecessary reconnects
        if (isMarketHours && timeSinceLastTick > 15000) {
          // First check if WebSocket connection is actually healthy
          if (ws.readyState !== WebSocket.OPEN) {
            console.warn(`[WebSocket] Connection lost - state: ${ws.readyState}. Reconnecting...`);
            ws.close();
          } else if (timeSinceLastTick > 30000) {
            // If still no ticks after 30 seconds even though socket is open, force reconnect
            console.warn(`[WebSocket] Connection stale - no ticks for ${Math.round(timeSinceLastTick/1000)}s despite open socket. Force reconnecting...`);
            ws.close();
          } else {
            // Connection appears open but no ticks - send a ping to verify
            try {
              ws.send(JSON.stringify({ type: "ping", ts: Date.now(), check: true }));
            } catch (e) {
              console.warn('[WebSocket] Ping failed, reconnecting...');
              ws.close();
            }
          }
        }
      }, 10000); // Check every 10 seconds instead of 3
      
    } catch (error) {
      console.error("WebSocket connection failed:", error);
      setWsConnected(false);
    }
  }, [isAuthenticated, isSwitchingBroker, playAlertSound, showNotification]);

  // Fallback polling for tick data when WebSocket not connected
  const pollTickData = useCallback(async () => {
    if (!isAuthenticated) return;
    
    const { ticks, market_status, is_weekend } = await fetchTicksAPI();
    
    // Update market status
    setMarketStatus(prev => ({
      ...prev,
      isOpen: market_status === 'open',
      isWeekend: is_weekend
    }));
    
    if (ticks.length > 0) {
      setTickData(prevTickData => {
        const tickMap = {};
        ticks.forEach(tick => {
          const prevTick = prevTickData[tick.instrument_token];
          // Only show price change animation if market is open for this instrument
          const showPriceChange = tick.market_open && prevTick;
          tickMap[tick.instrument_token] = {
            ...tick,
            priceChange: showPriceChange ? 
              (tick.last_price > prevTick.last_price ? "up" : 
               tick.last_price < prevTick.last_price ? "down" : null) : null
          };
        });
        return tickMap;
      });
    }
  }, [isAuthenticated]);

  // Effect: Check auth on mount and handle OAuth callback
  useEffect(() => {
    // Check for OAuth callback success
    const urlParams = new URLSearchParams(window.location.search);
    const loginStatus = urlParams.get('login');
    const userName = urlParams.get('user');
    const error = urlParams.get('error');
    
    if (loginStatus === 'success') {
      toast.success(`Welcome back, ${userName || 'User'}! Live data enabled.`);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    if (error) {
      toast.error(`Login failed: ${error}`);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    checkAuth();
  }, [checkAuth]);

  // Effect: Connect WebSocket when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      connectWebSocket();
    }
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [isAuthenticated, connectWebSocket]);

  // Effect: Check market status periodically
  useEffect(() => {
    if (!isAuthenticated) return;
    
    // Initial check
    checkMarketStatus();
    
    // Check every 30 seconds
    const intervalId = setInterval(checkMarketStatus, 30000);
    
    return () => clearInterval(intervalId);
  }, [isAuthenticated, checkMarketStatus]);

  // Effect: Check backend service status periodically
  // OPTIMIZED: Check every 30 seconds instead of 10 to reduce CPU usage
  useEffect(() => {
    if (!isAuthenticated) return;
    
    // Initial check
    fetchBackendServiceStatus();
    
    // Check every 30 seconds (was 10 seconds - too frequent)
    const intervalId = setInterval(fetchBackendServiceStatus, 30000);
    
    return () => clearInterval(intervalId);
  }, [isAuthenticated, fetchBackendServiceStatus]);

  // Effect: Poll tick data as fallback (when WebSocket is not available)
  // OPTIMIZED: Only poll when WebSocket is NOT connected to reduce CPU usage
  useEffect(() => {
    if (!isAuthenticated) return;
    
    // If WebSocket is connected, no need for polling - skip entirely
    if (wsConnected) {
      console.log('[App] WebSocket connected - skipping HTTP polling');
      return;
    }
    
    // If Zerodha broker is not connected, poll less frequently (every 5 seconds)
    // This is just for status checks, not live data
    const pollInterval = connectedBroker ? 1000 : 5000;
    
    // Initial poll
    pollTickData();
    
    // Set up polling interval - only as fallback when WebSocket is down
    const intervalId = setInterval(() => {
      // Skip polling when ALL markets are closed to save CPU
      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();
      const day = now.getDay();
      const timeInMinutes = hours * 60 + minutes;
      
      // Market hours check - poll if ANY market is open:
      // NSE/NFO/BSE/BFO: 9:15 AM (555 min) to 3:30 PM (930 min)
      // MCX: 9:00 AM (540 min) to 11:54 PM (1434 min)
      const isWeekend = day === 0 || day === 6;
      const isNseOpen = timeInMinutes >= 555 && timeInMinutes <= 930;
      const isMcxOpen = timeInMinutes >= 540 && timeInMinutes <= 1434;
      
      if (isWeekend || (!isNseOpen && !isMcxOpen)) {
        return; // Skip polling outside market hours
      }
      
      // Double-check WebSocket isn't connected before polling
      if (wsRef.current?.readyState !== WebSocket.OPEN) {
        pollTickData();
      }
    }, pollInterval);
    
    console.log(`[App] HTTP polling started (interval: ${pollInterval}ms, wsConnected: ${wsConnected}, broker: ${connectedBroker})`);
    
    return () => {
      clearInterval(intervalId);
      console.log('[App] HTTP polling stopped');
    };
  }, [isAuthenticated, wsConnected, connectedBroker, pollTickData]);

  // ==================== BACKGROUND TICK COLLECTION EFFECTS ====================
  
  // Fetch instruments when broker is connected
  useEffect(() => {
    if (isAuthenticated && connectedBroker) {
      console.log(`[App] Fetching instruments for broker: ${connectedBroker}`);
      fetchInstruments(connectedBroker);
    }
  }, [isAuthenticated, connectedBroker, fetchInstruments]);
  
  // Background tick collection - processes tickData and stores to history
  // This runs ALWAYS regardless of whether TickHistory modal is open
  useEffect(() => {
    if (!isAuthenticated || Object.keys(tickData).length === 0) return;
    
    // Weekend check - clear all cached data and skip processing
    const now = new Date();
    const dayOfWeek = now.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      // Weekend - clear background tick history
      if (Object.keys(backgroundTickHistoryRef.current).length > 0) {
        backgroundTickHistoryRef.current = {};
        console.log('[App] Weekend: Cleared background tick history');
      }
      return;
    }
    
    backgroundCollectionActiveRef.current = true;
    
    // Process incoming ticks and add to background history
    Object.entries(tickData).forEach(([tokenStr, tick]) => {
      if (!tick || !tick.timestamp_ms) return;
      
      const token = parseInt(tokenStr);
      const instrument = instruments[token];
      const exchange = getExchangeFromInstrument(instrument);
      
      // Only collect during market hours
      if (!isWithinMarketHours(tick.timestamp_ms, exchange)) return;
      
      // Initialize history array for token if needed
      if (!backgroundTickHistoryRef.current[token]) {
        backgroundTickHistoryRef.current[token] = [];
      }
      
      const history = backgroundTickHistoryRef.current[token];
      
      // Only add if it's actually a new tick - check by full tick ID (ts + ltq + ltp)
      // This allows multiple ticks per millisecond with different LTQ/LTP
      const tickId = `${token}_${tick.timestamp_ms}_${tick.last_traded_quantity || 0}_${tick.last_price || 0}`;
      const existsInHistory = history.length > 0 && 
        history.slice(0, 10).some(h => { // Only check recent 10 to avoid O(n) scan
          const hId = `${token}_${h.timestamp_ms}_${h.last_traded_quantity || 0}_${h.last_price || 0}`;
          return hId === tickId;
        });
      
      if (!existsInHistory) {
        // Prepend new tick and limit to 200000 per symbol
        backgroundTickHistoryRef.current[token] = [tick, ...history].slice(0, 200000);
      }
    });
    
    return () => {
      backgroundCollectionActiveRef.current = false;
    };
  }, [isAuthenticated, tickData, instruments]);
  
  // ─────────────────────────────────────────────────────────────────────────────
  // PERF MODE V2 — Periodic prune of inactive symbols from background buffer.
  // Toggle off any time via localStorage.setItem('perf_mode_v2','off') and reload.
  // Removes tick history for tokens not in user's current watchlist (default OR my).
  // Runs every 5 mins. Keeps RAM bounded across long trading days.
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated) return;
    if (typeof window !== 'undefined' && window.localStorage?.getItem('perf_mode_v2') === 'off') {
      return; // user opted out
    }
    const pruneInterval = setInterval(() => {
      try {
        // Collect "active" tokens = anything user is actually watching
        const activeTokens = new Set();

        // From default + my watchlist (persisted in localStorage by Dashboard)
        try {
          const def = JSON.parse(window.localStorage.getItem('defaultTokens') || '[]');
          def.forEach(t => activeTokens.add(parseInt(t)));
        } catch (_) {}
        try {
          const mine = JSON.parse(window.localStorage.getItem('myWatchlistTokens') || '[]');
          mine.forEach(t => activeTokens.add(parseInt(t)));
        } catch (_) {}
        // Also keep tokens currently in subscribed set if available globally
        try {
          if (window.__subscribedTokens && Array.isArray(window.__subscribedTokens)) {
            window.__subscribedTokens.forEach(t => activeTokens.add(parseInt(t)));
          }
        } catch (_) {}

        if (activeTokens.size === 0) return; // nothing to compare against; skip

        const before = Object.keys(backgroundTickHistoryRef.current).length;
        let removed = 0;
        Object.keys(backgroundTickHistoryRef.current).forEach(tokStr => {
          const tok = parseInt(tokStr);
          if (!activeTokens.has(tok)) {
            delete backgroundTickHistoryRef.current[tokStr];
            removed++;
          }
        });
        if (removed > 0) {
          console.log(`[Perf-V2 Prune] dropped ${removed}/${before} inactive token buffers (kept ${activeTokens.size})`);
        }
      } catch (err) {
        console.warn('[Perf-V2 Prune] error:', err);
      }
    }, 5 * 60 * 1000); // every 5 minutes

    return () => clearInterval(pruneInterval);
  }, [isAuthenticated]);

  // Periodic save to backend (every 30 seconds) - runs in background
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const saveInterval = setInterval(() => {
      // Skip saving when ALL markets are closed to save CPU
      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();
      const day = now.getDay();
      const timeInMinutes = hours * 60 + minutes;
      
      // Market hours check - save if ANY market is open:
      // NSE/NFO/BSE/BFO: 9:15 AM (555 min) to 3:30 PM (930 min)
      // MCX: 9:00 AM (540 min) to 11:54 PM (1434 min)
      const isWeekend = day === 0 || day === 6;
      const isNseOpen = timeInMinutes >= 555 && timeInMinutes <= 930;
      const isMcxOpen = timeInMinutes >= 540 && timeInMinutes <= 1434;
      
      if (isWeekend || (!isNseOpen && !isMcxOpen)) {
        return; // Skip saving outside market hours
      }
      
      const hasData = Object.keys(backgroundTickHistoryRef.current).length > 0 &&
        Object.values(backgroundTickHistoryRef.current).some(arr => arr.length > 0);
      
      if (hasData) {
        storeTicksToBackend();
      }
    }, 30000); // Save every 30 seconds
    
    // Also save on unmount/tab close
    const handleBeforeUnload = () => {
      const hasData = Object.keys(backgroundTickHistoryRef.current).length > 0;
      if (hasData) {
        // Use sendBeacon for reliable saves on tab close
        const batches = [];
        for (const [tokenStr, ticks] of Object.entries(backgroundTickHistoryRef.current)) {
          const token = parseInt(tokenStr);
          const lastIdx = lastStoredIndexRef.current[token] || 0;
          const newTicks = ticks.slice(lastIdx);
          if (newTicks.length > 0) {
            batches.push({
              token,
              symbol: instruments[token]?.tradingsymbol || `Token_${token}`,
              ticks: newTicks
            });
          }
        }
        if (batches.length > 0) {
          navigator.sendBeacon(
            `${API}/history/ticks/store`,
            JSON.stringify({ batches })
          );
        }
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      clearInterval(saveInterval);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isAuthenticated, instruments, storeTicksToBackend]);

  // Fetch alerts
  const fetchAlerts = useCallback(async () => {
    try {
      const response = await apiClient.get("/alerts");
      setAlerts(response.data.alerts || []);
      setTriggeredAlerts(response.data.triggered || []);
    } catch (error) {
      console.error("Failed to fetch alerts:", error);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchAlerts();
    }
  }, [isAuthenticated, fetchAlerts]);

  // Login handler
  const handleLogin = useCallback(async (credentials) => {
    try {
      const response = await apiClient.post("/user/login", credentials);
      if (response.data.success) {
        // Clear any previous user's state FIRST
        // This is important when switching users on the same device
        localStorage.removeItem('active_broker');
        localStorage.removeItem('kite_oauth_state');
        setTickData({});
        setAlerts([]);
        setTriggeredAlerts([]);
        setSubscribedTokens([]);
        
        // Close any existing WebSocket
        if (wsRef.current) {
          wsRef.current.close();
          wsRef.current = null;
        }
        setWsConnected(false);
        
        // Now set the new user's data
        localStorage.setItem('auth_token', response.data.access_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        localStorage.setItem('session_id', response.data.session_id || '');
        setCurrentUser(response.data.user);
        setIsAuthenticated(true);
        
        // Check if user has Zerodha connected - use the NEW user's status
        const isKiteConnected = response.data.user.kite_connected || false;
        
        setKiteConnected(isKiteConnected);
        // Track Zerodha connection
        if (isKiteConnected) {
          setConnectedBroker('zerodha');
        } else {
          setConnectedBroker(null);
        }
        
        // Show broker connect screen if not connected
        if (!isKiteConnected) {
          setShowKiteConnect(true);
        }
        
        toast.success("Login successful!");
        return { success: true };
      } else {
        toast.error(response.data.message || "Login failed");
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Login failed";
      toast.error(message);
      return { success: false, message };
    }
  }, []);

  // Login with default credentials (for admin/owner Zerodha connection)
  const handleDefaultLogin = useCallback(async () => {
    try {
      const response = await apiClient.post("/auth/login-default");
      if (response.data.success) {
        toast.success("Zerodha connected!");
        return { success: true };
      } else {
        toast.error(response.data.message || "Connection failed");
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Connection failed";
      toast.error(message);
      return { success: false, message };
    }
  }, []);

  // Logout handler
  const handleLogout = useCallback(async () => {
    // Clear ALL localStorage - auth, user, and broker data
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    localStorage.removeItem('session_id');
    localStorage.removeItem('active_broker');
    localStorage.removeItem('kite_oauth_state');
    
    // Broadcast logout to all pop-out windows
    broadcastLogout('manual');
    
    // Clear local state
    setIsAuthenticated(false);
    setCurrentUser(null);
    setTickData({});
    setAlerts([]);
    setTriggeredAlerts([]);
    setSubscribedTokens([]);
    setWsConnected(false);
    setKiteConnected(false);  // Reset Kite connection
    setShowKiteConnect(false);
    setConnectedBroker(null);  // Reset connected broker
    
    // Close WebSocket
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    toast.success("Logged out successfully");
  }, []);

  // Switch broker handler - allows user to change broker without full logout
  const handleSwitchBroker = useCallback(async () => {
    // Set flag to prevent auto-reconnection
    setIsSwitchingBroker(true);
    
    // Keep user authenticated but show broker selector
    setKiteConnected(false);
    setShowKiteConnect(true);
    setConnectedBroker(null);
    
    // Clear reconnection timeout if any
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    // Close WebSocket to stop data feed
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    // Clear tick data 
    setTickData({});
    setWsConnected(false);
    
    toast.info("Select your broker to continue");
  }, []);

  // Refresh user data (after subscription update)
  const refreshUser = useCallback(async () => {
    try {
      const response = await apiClient.get("/user/me");
      setCurrentUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
    } catch (err) {
      console.error("Failed to refresh user:", err);
    }
  }, []);

  // Subscribe to instruments - Zerodha only
  const subscribeInstruments = useCallback(async (tokens, instrumentInfo = []) => {
    try {
      // Use Zerodha subscribe endpoint
      const response = await apiClient.post("/subscribe", {
        instrument_tokens: tokens,
        mode: "full"
      });
      
      setSubscribedTokens(prev => [...new Set([...prev, ...tokens])]);
      toast.success(`Subscribed to ${tokens.length} instrument(s)`);
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Subscription failed");
      throw error;
    }
  }, []);

  // Unsubscribe from instruments - Zerodha only
  const unsubscribeInstruments = useCallback(async (tokens, instrumentInfo = []) => {
    try {
      // Use Zerodha unsubscribe endpoint
      const response = await apiClient.post("/unsubscribe", {
        instrument_tokens: tokens
      });
      
      setSubscribedTokens(prev => prev.filter(t => !tokens.includes(t)));
      setTickData(prev => {
        const newData = { ...prev };
        tokens.forEach(t => delete newData[t]);
        return newData;
      });
      toast.success(`Unsubscribed from ${tokens.length} instrument(s)`);
      return response.data;
    } catch (error) {
      toast.error("Unsubscription failed");
      throw error;
    }
  }, []);

  // Create alert
  const createAlert = useCallback(async (alertConfig) => {
    try {
      const response = await apiClient.post("/alerts", alertConfig);
      setAlerts(prev => [...prev, response.data.alert]);
      toast.success("Alert created");
      return response.data;
    } catch (error) {
      toast.error("Failed to create alert");
      throw error;
    }
  }, []);

  // Delete alert
  const deleteAlert = useCallback(async (alertId) => {
    try {
      await apiClient.delete(`/alerts/${alertId}`);
      setAlerts(prev => prev.filter(a => a.id !== alertId));
      toast.success("Alert deleted");
    } catch (error) {
      toast.error("Failed to delete alert");
      throw error;
    }
  }, []);

  const contextValue = {
    isAuthenticated,
    isLoading,
    currentUser,
    hasPremium,
    tickData,
    alerts,
    triggeredAlerts,
    wsConnected,
    subscribedTokens,
    marketStatus,
    backendServiceStatus,  // Backend service status
    handleLogin,
    handleDefaultLogin,
    handleLogout,
    handleSwitchBroker,  // New: Switch broker without full logout
    connectedBroker,     // Track Zerodha connection: 'zerodha' | null
    refreshUser,
    subscribeInstruments,
    unsubscribeInstruments,
    createAlert,
    deleteAlert,
    fetchAlerts,
    playAlertSound,
    checkMarketStatus,
    // Background tick collection data
    instruments,
    backgroundTickHistoryRef,
    backgroundStats,
  };

  // Handle OAuth callback from URL (for admin Zerodha connection ONLY)
  // User-level Zerodha callback is handled by ConnectZerodha.jsx
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const loginSuccess = urlParams.get('login');
    
    // If login=success, the backend already handled it (admin Zerodha)
    if (loginSuccess === 'success') {
      toast.success('Zerodha connected successfully!');
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }
    
    // NOTE: request_token handling for user-level auth is done in ConnectZerodha.jsx
    // Do NOT handle it here to avoid duplicate token exchange attempts
  }, []);

  if (isLoading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-[#0A0A0A]">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#A1A1AA]">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AppContext.Provider value={contextValue}>
      <div className="h-screen w-screen overflow-hidden bg-[#0A0A0A]">
        <Toaster 
          position="top-right" 
          richColors 
          theme="dark"
          toastOptions={{
            style: {
              background: '#111111',
              border: '1px solid #27272A',
            },
          }}
        />
        
        {/* SEBI Risk Disclaimer Popup */}
        {showSebiDisclaimer && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90 backdrop-blur-sm">
            <div className="bg-[#0a0e17] border border-amber-500/40 rounded-2xl p-6 w-[90%] max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
              <div className="text-center mb-4">
                <div className="w-14 h-14 mx-auto rounded-xl bg-amber-500/20 flex items-center justify-center mb-3">
                  <svg className="w-8 h-8 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">SEBI Risk Disclosure</h3>
                <p className="text-xs text-amber-400 font-semibold">Important: Please Read Carefully</p>
              </div>
              
              <div className="space-y-3 text-sm text-[#a1a1aa] mb-6 bg-[#111827] p-4 rounded-lg border border-[#1a2540]">
                <p className="font-semibold text-white">Investment in securities market are subject to market risks.</p>
                
                <p>• Trading in equity, derivatives and commodities involves substantial risk of loss and is not suitable for every investor.</p>
                
                <p>• The valuation of securities may increase or decrease depending on the market conditions.</p>
                
                <p>• Past performance is not indicative of future results.</p>
                
                <p>• Please read all scheme related documents carefully before investing.</p>
                
                <p className="text-amber-400 font-medium">• As per SEBI guidelines, 9 out of 10 individual traders in equity Futures and Options segment incurred net losses.</p>
                
                <p>• The amount of loss may be more than the initial investment.</p>
                
                <p className="text-xs text-[#6b7a99] mt-4 pt-3 border-t border-[#1a2540]">
                  By clicking "I Understand & Accept", you acknowledge that you have read and understood the above risk disclosure and agree to proceed at your own risk.
                </p>
              </div>
              
              <button
                onClick={() => {
                  setShowSebiDisclaimer(false);
                  // Mark disclaimer as accepted in localStorage
                  localStorage.setItem('sebi_disclaimer_accepted', 'true');
                  // Complete the login process
                  if (pendingLoginData) {
                    localStorage.setItem('auth_token', pendingLoginData.access_token);
                    localStorage.setItem('user', JSON.stringify(pendingLoginData.user));
                    setCurrentUser(pendingLoginData.user);
                    setIsAuthenticated(true);
                    const isKiteConnected = pendingLoginData.user.kite_connected || false;
                    setKiteConnected(isKiteConnected);
                    if (isKiteConnected) {
                      setConnectedBroker('zerodha');
                    } else {
                      setConnectedBroker(null);
                      setShowKiteConnect(true);
                    }
                    setPendingLoginData(null);
                  }
                }}
                className="w-full py-3 px-4 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold rounded-xl transition-all duration-200 shadow-lg shadow-amber-500/25"
                data-testid="sebi-accept-btn"
              >
                I Understand & Accept
              </button>
            </div>
          </div>
        )}
        
        {/* Per-User Broker Connection Gate */}
        {isAuthenticated && showKiteConnect && !kiteConnected && (
          <BrokerSelector 
            user={currentUser}
            forceSelection={isSwitchingBroker}  // Force selection screen when switching brokers
            onConnected={(brokerData) => {
              setKiteConnected(true);
              setShowKiteConnect(false);
              setIsSwitchingBroker(false);  // Reset switching flag
              // Set Zerodha as the connected broker
              setConnectedBroker('zerodha');
              const userName = brokerData.kite_user_name || brokerData.kite_user_id || brokerData.user_name || 'your account';
              toast.success(`Connected to Zerodha: ${userName}`);
              // Refresh user data
              refreshUser();
            }}
            onLogout={() => {
              // Logout and go back to login page
              handleLogout();
            }}
          />
        )}
        
        <BrowserRouter>
          <Routes>
            <Route 
              path="/login" 
              element={
                isAuthenticated ? <Navigate to="/" replace /> : <Login onLoginSuccess={(data) => {
                  // Check if SEBI disclaimer was already accepted
                  const disclaimerAccepted = localStorage.getItem('sebi_disclaimer_accepted') === 'true';
                  
                  if (!disclaimerAccepted) {
                    // Show SEBI disclaimer first, store login data for later
                    setPendingLoginData(data);
                    setShowSebiDisclaimer(true);
                  } else {
                    // Disclaimer already accepted, proceed with login
                    localStorage.setItem('auth_token', data.access_token);
                    localStorage.setItem('user', JSON.stringify(data.user));
                    setCurrentUser(data.user);
                    setIsAuthenticated(true);
                    // Check Zerodha connection status
                    const isKiteConnected = data.user.kite_connected || false;
                    setKiteConnected(isKiteConnected);
                    // Track Zerodha connection
                    if (isKiteConnected) {
                      setConnectedBroker('zerodha');
                    } else {
                      setConnectedBroker(null);
                      setShowKiteConnect(true);
                    }
                  }
                }} />
              } 
            />
            <Route 
              path="/activity-log" 
              element={
                isAuthenticated && kiteConnected ? <ActivityLogPage /> : 
                isAuthenticated ? <Navigate to="/" replace /> : <Navigate to="/login" replace />
              } 
            />
            <Route 
              path="/popout" 
              element={<PopOutTickLog />} 
            />
            <Route
              path="/algo-setup-popout"
              element={isAuthenticated ? <AlgoSetupPopOut /> : <Navigate to="/login" replace />}
            />
            <Route 
              path="/dom-analysis" 
              element={
                isAuthenticated && kiteConnected ? 
                  <DOMAnalysis /> : 
                  <Navigate to="/" replace />
              } 
            />
            <Route 
              path="/option-chain" 
              element={
                isAuthenticated && kiteConnected ? 
                  <OptionChain /> : 
                  <Navigate to="/" replace />
              } 
            />
            <Route 
              path="/*" 
              element={
                isAuthenticated ? 
                  (kiteConnected ? <Dashboard /> : <div className="h-full w-full" />) : 
                  <Navigate to="/login" replace />
              } 
            />
          </Routes>
        </BrowserRouter>
      </div>
    </AppContext.Provider>
  );
}

export default App;
